import React, { useCallback, useMemo, useState } from "react";
import { Flex, SearchOption, TypeaheadInput } from "@jsq/ds/core";
import { formatError, useReportError } from "#common/hooks/useReportError";
import { useGetEntityByArenaFilteredLazyQuery } from "#graphql/supergraphHooks";
import { clientContext, toDbId } from "#utils/graphql_utils";
import { EntityBankAccountDisplay } from "./EntityBankAccountDisplay/index";
import { EntityBankAccountType, EntityEdgeType } from "./types";

export interface EntitySelectorProps {
  arenaId?: string;
  selectedEntityName?: string;
  onEntitySelect: (
    entityId: string,
    entityName: string,
    bankAccount?: EntityBankAccountType | null,
  ) => void;
  disabled?: boolean;
  isFundAdmin?: boolean;
  hasBankAccount?: boolean;
  withFidesPaymentsCapabilityOnly?: boolean;
  showBankAccountDisplay?: boolean;
}

/**
 * EntitySelector component for selecting investment entities.
 * Requires an arenaId to fetch entities for that specific client/arena.
 *
 * This component is decoupled and can be used in any workflow that needs entity selection.
 */
export const EntitySelector: React.FC<EntitySelectorProps> = ({
  arenaId,
  selectedEntityName,
  onEntitySelect,
  disabled = false,
  isFundAdmin,
  hasBankAccount,
  withFidesPaymentsCapabilityOnly,
  showBankAccountDisplay = true,
}) => {
  const [fetchEntityQuery, { data, loading, error }] = useGetEntityByArenaFilteredLazyQuery();
  const [selectedEntity, setSelectedEntity] = useState<EntityEdgeType["node"] | null>(null);

  useReportError(error ? formatError(error) : null);

  // Extract entity list from GraphQL response
  const entityOptions: SearchOption[] = useMemo(
    () =>
      data?.investmentEntities?.edges.map((entity: EntityEdgeType) => ({
        id: entity.node.id,
        label: entity.node.name,
      })) || [],
    [data],
  );

  // Handle search with debouncing
  const handleDebouncedSearch = useCallback(
    async (term: string) => {
      if (!arenaId || term.length < 1) {
        return;
      }
      const arenaIdsDB = String(toDbId(arenaId));
      const variables = {
        filters: {
          arenaIds: [arenaIdsDB],
          nameSearchTerm: term,
          isFundAdmin: isFundAdmin,
          hasBankAccount: hasBankAccount,
          withFidesPaymentsCapabilityOnly: withFidesPaymentsCapabilityOnly,
        },
      };

      await fetchEntityQuery({
        context: clientContext(),
        variables,
        fetchPolicy: "cache-and-network",
      });
    },
    [arenaId, fetchEntityQuery, isFundAdmin, hasBankAccount, withFidesPaymentsCapabilityOnly],
  );

  // Handle entity selection
  const handleEntitySelect = useCallback(
    (selectedOption?: SearchOption) => {
      if (selectedOption) {
        // Find the full entity data from the GraphQL response
        const entityData = data?.investmentEntities?.edges.find(
          (entity: EntityEdgeType) => entity.node.id === selectedOption.id,
        );

        if (entityData) {
          setSelectedEntity(entityData.node);
        }

        // Pass bank account data to the callback
        const bankAccount = entityData?.node?.bankAccount as EntityBankAccountType | null;
        onEntitySelect(String(selectedOption.id), selectedOption.label, bankAccount);
      } else {
        // Clear selection when option is cleared
        setSelectedEntity(null);
      }
    },
    [onEntitySelect, data],
  );

  // Show info message if no arena is selected
  if (!arenaId) {
    return (
      <TypeaheadInput
        disabled
        isFullWidth
        placeholder="Please select a client first"
        options={[]}
        onDebouncedSearch={() => {}}
        onSelectOption={() => {}}
      />
    );
  }

  return (
    <Flex flexDirection="column" gap={8}>
      <TypeaheadInput
        disabled={disabled}
        isClearable
        isFullWidth
        isLoading={loading}
        options={entityOptions}
        onDebouncedSearch={handleDebouncedSearch}
        onSelectOption={handleEntitySelect}
        placeholder="Search for an entity"
        minLength={1}
        initialSearchValue={selectedEntityName}
      />

      {showBankAccountDisplay && <EntityBankAccountDisplay selectedEntity={selectedEntity} />}
    </Flex>
  );
};
